import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { WaiverFormConfig } from './waiver-builder.model';
import { WaiverBuilderService } from './waiver-builder.service';
import { EnvelopeService } from './envelope.service';
import { EnvelopeItem } from './envelope.model';
import { classificationForNewRequirement } from './waiver-compliance';

interface SignFormData {
  [fieldId: string]: unknown;
}

@Component({
  selector: 'app-waiver-sign',
  templateUrl: './waiver-sign.component.html',
  styleUrls: ['./waiver-sign.component.scss']
})
export class WaiverSignComponent implements OnInit, OnDestroy {

  private routeSub?: Subscription;

  @ViewChild('signForm') signForm: NgForm;

  protected waiver: WaiverFormConfig | null = null;
  /** Sanitized `<iframe>` src for the waiver's attached PDF (when set).
   *  Rebuilt in `loadForCurrentRoute` alongside the waiver load. */
  protected pdfSafeUrl: SafeResourceUrl | null = null;
  protected loading = true;
  protected notFound = false;
  protected formData: SignFormData = {};
  /** Derived from the envelope participant's date of birth; `null` on the
   *  anonymous path, where there is no participant to derive from.
   *  Replaces the retired `isUnder18` declaration. */
  protected signerIsMinor: boolean | null = null;
  protected submitted = false;
  protected submitting = false;
  protected validationError = '';
  protected invalidFieldIds: Set<string> = new Set();
  protected loadError = '';
  /** Populated when the server refused to record the signature. Renders
   *  a retry-able error state instead of the success confirmation so the
   *  customer knows their signature didn't go through. Cleared when the
   *  form is edited or a fresh submit is attempted. */
  protected submitError = '';

  // Prefill data from query params
  private prefillName = '';
  private prefillEmail = '';
  private prefillPhone = '';
  /**
   * The guardian the minor was added with — a starting value for the
   * waiver's guardian name field, so they need not type it again. Only for
   * a known minor; the signer can change it, and what is on the form when
   * they sign is what `signedBy.guardianName` records.
   */
  private prefillGuardianName = '';

  // Envelope context — when signing as part of an envelope, these identify
  // which item within which envelope the submission should be recorded against.
  protected envelopeId: string | null = null;
  protected envelopeItemId: string | null = null;
  protected envelopeItem: EnvelopeItem | null = null;
  /** Snapshot of every item in the envelope, used to drive the prev/next
   *  navigation header at the top of the form. Empty in non-envelope flows. */
  protected envelopeItems: EnvelopeItem[] = [];

  /**
   * A link from the retired self-service signing scheme: it carries a
   * booking reference but no envelope item, so there is nothing for a
   * signature to be recorded against. Such links were printed on physical
   * cards, so they keep arriving after the scheme was withdrawn. The page
   * refuses them outright rather than rendering a form whose submission
   * could not persist — a signer who filled one in would be told they had
   * signed while nothing was saved.
   *
   * Narrow by construction: envelope context is read FIRST and wins, so a
   * legitimate envelope link that happens to also carry a booking
   * reference is unaffected.
   */
  protected retiredLink = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private waiverBuilderService: WaiverBuilderService,
    private envelopeService: EnvelopeService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    // Subscribe to route changes so prev/next navigation between items on
    // the SAME WaiverSignComponent instance (Angular reuses the component
    // when only path params change) properly re-loads the form. Initial
    // load is also handled here — paramMap emits synchronously on subscribe.
    // queryParams aren't streamed separately because the route snapshot
    // reflects the new values by the time paramMap emits for the same
    // navigation event.
    this.routeSub = this.route.paramMap.subscribe(() => {
      this.loadForCurrentRoute().catch(err => {
        console.error('Failed to (re)load waiver for current route:', err);
      });
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  private async loadForCurrentRoute(): Promise<void> {
    // Reset per-item state so prev/next navigation gets a clean form.
    this.waiver = null;
    this.pdfSafeUrl = null;
    this.loading = true;
    this.notFound = false;
    this.loadError = '';
    this.formData = {};
    this.signerIsMinor = null;
    this.prefillGuardianName = '';
    this.submitted = false;
    this.submitting = false;
    this.submitError = '';
    this.validationError = '';
    this.invalidFieldIds.clear();
    this.envelopeItem = null;
    this.envelopeItems = [];
    this.retiredLink = false;

    const waiverId = this.route.snapshot.paramMap.get('waiverId');
    const queryParams = this.route.snapshot.queryParams;

    this.envelopeId = queryParams['envelopeId'] || null;
    this.envelopeItemId = queryParams['itemId'] || null;

    // Envelope context takes precedence for prefill, so a recipient can't
    // spoof participant identity by editing the URL. The participant lives on
    // the envelope (one envelope = one customer) — not on the item.
    if (this.envelopeId && this.envelopeItemId) {
      const envelope = await this.envelopeService.getEnvelopeById(this.envelopeId);
      const item = envelope?.items.find(i => i.itemId === this.envelopeItemId);
      if (!envelope || !item || item.waiverId !== waiverId) {
        this.loading = false;
        this.notFound = true;
        this.loadError = 'This envelope item is invalid or does not match the waiver.';
        return;
      }
      if (item.status === 'signed') {
        // Already signed — bounce straight back to the envelope overview.
        this.router.navigate(['/envelope', this.envelopeId], { replaceUrl: true });
        return;
      }
      this.envelopeItem = item;
      this.envelopeItems = envelope.items;
      this.prefillName = envelope.participant.name || '';
      this.prefillEmail = envelope.participant.email || '';
      this.prefillPhone = envelope.participant.phone || '';
      // Prefers current age, falling back to the issued `isMinor` for legacy
      // participants with no date of birth.
      this.signerIsMinor = classificationForNewRequirement(envelope.participant);
      this.prefillGuardianName = this.signerIsMinor === true
        ? (envelope.participant.guardianName || '').trim()
        : '';
    } else {
      this.prefillName = queryParams['name'] || '';
      this.prefillEmail = queryParams['email'] || '';
      this.prefillPhone = queryParams['phone'] || '';
      // Retired self-service link. Detected only here, in the branch with
      // NO envelope item, so the shared /sign-waiver route keeps working
      // for every legitimate producer (envelope email, Copy Link, short
      // link, generateSigningLink). Returns before the waiver is fetched:
      // there is no reason to load a document nobody may sign.
      if (queryParams['rentalId']) {
        this.retiredLink = true;
        this.loading = false;
        return;
      }
    }

    if (!waiverId) {
      this.loading = false;
      this.notFound = true;
      return;
    }

    let waiver: WaiverFormConfig | undefined;
    try {
      // Envelope context with a pinned version → load THAT snapshot from
      // /versions/{N} so the customer sees the exact form they were sent,
      // even if the partner edited the waiver in the meantime. Fall back
      // to the current parent doc when the pinned version isn't set
      // (legacy envelopes created before pinning) or the snapshot can't
      // be found (e.g. the version got deleted, which shouldn't normally
      // happen but is worth guarding against).
      if (this.envelopeItem && typeof this.envelopeItem.waiverVersion === 'number') {
        waiver = await this.waiverBuilderService.getWaiverVersion(
          waiverId,
          this.envelopeItem.waiverVersion,
        );
      }
      // Anonymous sign sessions never hydrate the partner-side cache, so
      // fall back to a direct Firestore read on miss.
      if (!waiver) {
        waiver = await this.waiverBuilderService.getWaiverByIdAsync(waiverId);
      }
    } catch (err) {
      console.error('Failed to load waiver:', err);
      this.loading = false;
      this.notFound = true;
      return;
    }

    if (!waiver) {
      this.loading = false;
      this.notFound = true;
      return;
    }

    if (!waiver.fields || waiver.fields.length === 0) {
      this.loading = false;
      this.notFound = true;
      this.loadError = 'This waiver has no fields configured. Please contact the provider.';
      return;
    }

    this.waiver = waiver;

    // Trigger a background load of the company-wide minor-age threshold so
    // the "Is Under N" label matches what the shop configured. Fire-and-forget
    // — the service seeds the observable to DEFAULT_MINOR_AGE_THRESHOLD on
    // init, so the label renders sensibly before this resolves. Errors are
    // non-fatal (public sign flow keeps working with the default).
    if (waiver.companyId) {
      this.waiverBuilderService.loadMinorAgeThreshold(waiver.companyId).catch((err) => {
        console.error('Failed to load company minor-age threshold on public sign page:', err);
      });
    }

    this.pdfSafeUrl = waiver.pdfContent
      ? this.sanitizer.bypassSecurityTrustResourceUrl(waiver.pdfContent)
      : null;
    this.prefillFormData();
    this.loading = false;
  }

  private prefillFormData(): void {
    if (!this.waiver) return;

    for (const field of this.waiver.fields) {
      switch (field.type) {
        case 'name':
          this.formData[field.id] = this.prefillName;
          break;
        case 'email':
          this.formData[field.id] = this.prefillEmail;
          break;
        case 'phone':
          this.formData[field.id] = this.prefillPhone;
          break;
        case 'guardianName':
          // Never replace a name already on the form.
          this.formData[field.id] = this.formData[field.id] || this.prefillGuardianName;
          break;
        default:
          this.formData[field.id] = '';
          break;
      }

    }
  }

  /** Offered when the signer is a minor OR their age is unknown
   *  (anonymous), so a guardian always has somewhere to sign. Required only
   *  when known — see {@link guardianRequired}. */
  protected isGuardianVisible(): boolean {
    return this.signerIsMinor !== false;
  }

  /** Makes the guardian fields mandatory rather than merely available. */
  protected get guardianRequired(): boolean {
    return this.signerIsMinor === true;
  }

  /** What `signedByGuardian` records: an observable fact about the
   *  signature rather than a claim about the signer's age. */
  private guardianDidSign(): boolean {
    const field = this.waiver?.fields.find(f => f.type === 'guardianSignature');
    return !!field && !!this.formData[field.id];
  }

  /**
   * Records the signature server-side and ONLY then flips the UI into
   * the success state. Previously the success confirmation rendered
   * immediately after a fixed setTimeout while the network write was
   * still in flight (or, worse, silently failed) — meaning a customer
   * could sign a waiver, see "Waiver Submitted Successfully", and the
   * partner would never receive the signed doc. Now we await the write
   * and, on failure, surface a retry-able error state so the customer
   * knows the signature didn't go through.
   */
  protected async submitWaiver(): Promise<void> {
    if (this.submitted || this.submitting) return;
    // Belt and braces: the template renders no form in this state, so this
    // is only reachable by calling the handler directly. It stays because
    // the cost of a signature that silently persists nothing is high.
    if (this.retiredLink) return;

    if (!this.validateForm()) return;

    if (!this.waiver?.id) {
      this.validationError = 'This waiver is not properly configured. Please contact the provider.';
      return;
    }

    this.submitting = true;
    this.validationError = '';
    this.submitError = '';

    try {
      // Envelope context is the common path (customer clicked an email
      // link) and now the only path that persists anything. Awaited so a
      // network failure blocks the success flip.
      await this.recordEnvelopeSignatureIfApplicable();
      this.submitted = true;
    } catch (err) {
      console.error('Failed to record signature:', err);
      this.submitError =
        'We couldn’t save your signature. Please check your connection and try again.';
    } finally {
      this.submitting = false;
    }
  }

  private async recordEnvelopeSignatureIfApplicable(): Promise<void> {
    if (!this.envelopeId || !this.envelopeItemId || !this.waiver) return;

    const envelope = await this.envelopeService.getEnvelopeById(this.envelopeId);
    const participant = envelope?.participant;

    const signerName = this.readFirstFieldValue('name') || participant?.name || '';
    const signerEmail = this.readFirstFieldValue('email') || participant?.email;
    const signerPhone = this.readFirstFieldValue('phone') || participant?.phone;
    // Guardian name comes from the waiver's own `guardianName` input so the
    // signed-view display reflects who ACTUALLY signed, not the value the
    // partner pre-populated on the envelope at creation time (which may be
    // stale — e.g., the other parent ended up doing the signing).
    const guardianName = this.isGuardianVisible() ? this.readFirstFieldValue('guardianName') : undefined;

    await this.envelopeService.markItemSigned({
      envelopeId: this.envelopeId,
      itemId: this.envelopeItemId,
      signedBy: {
        name: signerName,
        email: signerEmail,
        phone: signerPhone,
        signedByGuardian: this.guardianDidSign(),
        ...(guardianName ? { guardianName } : {}),
      },
      // Snapshot the captured form state (signature canvas data URLs + any
      // text input the customer filled in) so the partner can later view
      // the rendered, signed waiver on the Waivers tab. Shallow-clone so
      // later mutations to `formData` (component reuse, navigation) don't
      // leak through the payload.
      signedFormData: { ...this.formData },
    });
  }

  /**
   * True when the given phone field's typed value has fewer than 10 digits
   * after stripping non-digit characters. Empty is treated as valid — the
   * required-field check above handles empty-when-required separately.
   * Bound in the template to render an inline "at least 10 digits" hint
   * below the phone input.
   */
  protected isPhoneFormatInvalid(fieldId: string): boolean {
    const raw = this.formData[fieldId];
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) return false;
    return value.replace(/\D/g, '').length < 10;
  }

  /**
   * True when the given email field's typed value doesn't match
   * `local@domain.tld` shape. Empty is treated as valid (the required
   * check handles empty separately). Drives the inline hint under the
   * email input.
   */
  protected isEmailFormatInvalid(fieldId: string): boolean {
    const raw = this.formData[fieldId];
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) return false;
    return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private readFirstFieldValue(type: 'name' | 'email' | 'phone' | 'guardianName'): string | undefined {
    const field = this.waiver?.fields.find(f => f.type === type);
    if (!field) return undefined;
    const value = this.formData[field.id];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  protected returnToEnvelope(): void {
    if (!this.envelopeId) return;
    this.router.navigate(['/envelope', this.envelopeId]);
  }

  protected get isEnvelopeContext(): boolean {
    return !!this.envelopeId && !!this.envelopeItemId;
  }

  /** Zero-based position of the current item in the envelope's items list,
   *  used by the navigation header to show "Item N of M". -1 outside the
   *  envelope context. */
  protected get currentItemIndex(): number {
    if (!this.envelopeItemId) return -1;
    return this.envelopeItems.findIndex(i => i.itemId === this.envelopeItemId);
  }

  /** Items the prev/next buttons can land on — exclude already-signed items
   *  since landing on one would just redirect back to the envelope (see the
   *  signed-item branch in ngOnInit). */
  private get pendingItems(): EnvelopeItem[] {
    return this.envelopeItems.filter(i => i.status !== 'signed');
  }

  /** Returns the pending item immediately before the current one in the
   *  envelope's items order, or null if the current item is the first
   *  pending one. The current item itself may already be signed (the form
   *  is still up before redirect lands) — handle that by looking up by
   *  position in the full items list, not in the filtered pending list. */
  protected get prevPendingItem(): EnvelopeItem | null {
    const idx = this.currentItemIndex;
    if (idx <= 0) return null;
    for (let i = idx - 1; i >= 0; i--) {
      const candidate = this.envelopeItems[i];
      if (candidate && candidate.status !== 'signed') return candidate;
    }
    return null;
  }

  /** Mirror of prevPendingItem but in the forward direction. */
  protected get nextPendingItem(): EnvelopeItem | null {
    const idx = this.currentItemIndex;
    if (idx < 0 || idx >= this.envelopeItems.length - 1) return null;
    for (let i = idx + 1; i < this.envelopeItems.length; i++) {
      const candidate = this.envelopeItems[i];
      if (candidate && candidate.status !== 'signed') return candidate;
    }
    return null;
  }

  protected goToPrev(): void {
    const target = this.prevPendingItem;
    if (target) this.goToItem(target);
  }

  protected goToNext(): void {
    const target = this.nextPendingItem;
    if (target) this.goToItem(target);
  }

  /** Hard-navigate to another item in the same envelope. We rely on
   *  `onSameUrlNavigation: 'reload'` (router-default for path changes;
   *  the waiverId IS changing here) so ngOnInit re-runs with fresh
   *  state — anything the user typed on the current item is discarded.
   *  Acceptable: the prev/next buttons live on the unsubmitted form, so
   *  the user is signaling they want to step away. */
  private goToItem(item: EnvelopeItem): void {
    if (!this.envelopeId) return;
    this.router.navigate(['/sign-waiver', item.waiverId], {
      queryParams: { envelopeId: this.envelopeId, itemId: item.itemId },
    });
  }

  private validateForm(): boolean {
    if (!this.waiver) return false;

    this.invalidFieldIds.clear();
    this.validationError = '';

    for (const field of this.waiver.fields) {
      // Skip non-input display fields
      if (field.type === 'textBody' || field.type === 'isUnder18') continue;

      // Guardian signature only required when under 18
      if (field.type === 'guardianSignature') {
        if (this.guardianRequired && !this.formData[field.id]) {
          this.invalidFieldIds.add(field.id);
        }
        continue;
      }

      // Guardian name only required when under 18 — paired with the signature.
      if (field.type === 'guardianName') {
        if (this.guardianRequired) {
          const value = this.formData[field.id];
          if (!value || (typeof value === 'string' && !value.trim())) {
            this.invalidFieldIds.add(field.id);
          }
        }
        continue;
      }

      // Signature is always required when present
      if (field.type === 'signature') {
        if (!this.formData[field.id]) {
          this.invalidFieldIds.add(field.id);
        }
        continue;
      }

      // Phone is ALWAYS optional at signing — even if the builder marked
      // the field as required, we let signers skip it (contact channels
      // are captured elsewhere). Format validation still runs when a
      // value IS entered so downstream envelope routing doesn't get an
      // unusable number.
      if (field.type === 'phone') {
        const value = this.formData[field.id];
        const strValue = typeof value === 'string' ? value.trim() : '';
        if (strValue && strValue.replace(/\D/g, '').length < 10) {
          this.invalidFieldIds.add(field.id);
        }
        continue;
      }

      // Email gets format validation on top of the required check — a
      // typed value has to look like `name@domain.tld` or downstream
      // notifications silently fail (SparkPost rejects malformed
      // recipients).
      if (field.type === 'email') {
        const value = this.formData[field.id];
        const strValue = typeof value === 'string' ? value.trim() : '';
        if (field.required && !strValue) {
          this.invalidFieldIds.add(field.id);
        } else if (strValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(strValue)) {
          this.invalidFieldIds.add(field.id);
        }
        continue;
      }

      // Standard required fields (name)
      if (field.required) {
        const value = this.formData[field.id];
        if (!value || (typeof value === 'string' && !value.trim())) {
          this.invalidFieldIds.add(field.id);
        }
      }
    }

    if (this.invalidFieldIds.size > 0) {
      this.validationError = `Please complete all required fields (${this.invalidFieldIds.size} missing).`;
      return false;
    }

    return true;
  }

  protected resetForm(): void {
    this.formData = {};
    this.signerIsMinor = null;
    this.submitted = false;
    this.submitting = false;
    this.submitError = '';
    this.validationError = '';
    this.invalidFieldIds.clear();
    this.prefillFormData();
  }

  protected get currentYear(): number {
    return new Date().getFullYear();
  }
}
